import { useQueryClient } from "@tanstack/react-query";
import * as jose from "jose";
import { UseFormTrigger } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { COUNTRY_CODES } from "~/common/constants/verificationCode";
import { DATA_TOKEN } from "~/patients/constants";
import { useSendEmailVerification } from "~/patients/hooks/queries/useSendEmailVerification";
import { ROUTES } from "~/patients/router";
import { useOnBoardingNewPatientStore } from "~/patients/stores/useOnBoardingNewPatientStore";
import {
  NewPatientSchemaType,
  SignUpNewPatientDataRequest,
  SignUpPatientSchemaType,
} from "~/patients/utils";
import { useFormatHook } from "../useFormatHook";
import { useCreateNewPatient } from "./useCreateNewPatient";

interface UseSubmitNewPatientSignUpProps {
  values: NewPatientSchemaType;
  trigger: UseFormTrigger<SignUpPatientSchemaType>;
  providerId?: string;
  organizationId?: string;
  invitationCode?: string;
}

export const useSubmitNewPatientSignUp = ({
  values,
  trigger,
  providerId,
  organizationId,
  invitationCode,
}: UseSubmitNewPatientSignUpProps) => {
  const { setNewPatient, resetStep, newPatient } =
    useOnBoardingNewPatientStore();

  const { stripPhoneNumber } = useFormatHook();

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { sendEmail } = useSendEmailVerification();

  const onSignUpSuccess = () => {
    queryClient.clear();
    navigate(ROUTES.verifyEmail);
    setNewPatient();
    resetStep();
    sendEmail({ showSuccessMessage: false });
  };

  const { mutate: signUpMutation, isPending } =
    useCreateNewPatient(onSignUpSuccess);

  const onSubmitSignUp = async () => {
    try {
      const isValidData = await trigger();
      if (!isValidData) return;

      if (!organizationId) {
        const dataToken = searchParams.get(DATA_TOKEN);

        if (dataToken) {
          const userJWT = jose.decodeJwt(dataToken);
          providerId = userJWT.provider_id as string | undefined;
          organizationId = userJWT.organizationId as string | undefined;

          if (!providerId)
            throw new Error("Invalid provider and organization ID");
        }
      }

      if (!newPatient?.one_use_token) {
        toast.error("Your verification token has expired");
        return;
      }

      const patientToCreate: SignUpNewPatientDataRequest = {
        ...values.namesAndEmail,
        ...values.date_of_birth,
        guardian_name: values.termsAndConditions.guardian_name,
        password: values.password.password,
        address: values.address,
        provider_id: providerId,
        organization_id: organizationId,
        invitation_code: invitationCode,
        phone_number: {
          number: stripPhoneNumber(values.phone_number.phone_number),
          country_code: COUNTRY_CODES.US,
        },
        verification_token: newPatient.one_use_token,
      };

      signUpMutation(patientToCreate);
    } catch (_error) {
      navigate(ROUTES.notFound);
    }
  };

  return {
    onSubmitSignUp,
    isPending,
  };
};
